<template>
  <VCard class="data-table" elevation="10">
    <VRow class="ma-2" dense align="center">
      <VCol v-if="props.title || props.tTitle" cols="auto">
        <h3>
          {{ props.tTitle || t(props.title) }}
          <VTooltip location="top">
            <template #activator="{ props: slotProps }">
              <VBtn icon="mdi-refresh" color="default" v-bind="slotProps" variant="text" size="small" @click="getData" />
            </template>
            <span>{{ $t('refresh') }}</span>
          </VTooltip>

          <ExportAttributeMenu v-if="props.exportModel" :export-model="props.exportModel" :data-object="props.filtersData" :search="data.search" />

          <slot name="afterReload" />
        </h3>
        <span v-if="props.tSubtitle || props.subtitle" class="opacity-50">{{ props.tSubtitle || t(props.subtitle) }}</span>
      </VCol>
      <VSpacer />
      <VCol cols="auto">
        <slot name="beforeSearch" />
      </VCol>
      <VCol v-if="props.enableSearch" cols="auto">
        <VTextField
          v-model="data.search"
          name="data-table-search-field"
          append-inner-icon="mdi-magnify"
          :placeholder="$t(props.searchLabel)"
          hide-details
          clearable
          style="min-width: 250px"
          class="max-width-350 ms-2"
          type="text"
          @update:model-value="onSearch" />
      </VCol>
      <VCol cols="auto">
        <slot name="buttons" />
      </VCol>
      <VCol v-if="props.enableFilters" cols="auto">
        <VTooltip location="top">
          <template #activator="{ props: slotProps }">
            <VBtn color="default" size="small" elevation="10" icon v-bind="slotProps" @click="data.showFilters = !data.showFilters">
              <VIcon icon="mdi-filter-outline" size="20" />
            </VBtn>
          </template>
          <span>{{ $t('filters') }}</span>
        </VTooltip>
      </VCol>
    </VRow>
    <VExpandTransition v-if="props.enableFilters && data.showFilters">
      <VCard class="ma-2" elevation="1" style="overflow: initial">
        <slot name="filters" />
      </VCard>
    </VExpandTransition>
    <VRow class="mb-2">
      <slot name="table-top" />
      <VCol cols="12">
        <v-data-table
          v-model="selectedItems"
          class="border-t-sm border-b-sm border-x-0 overflow-hidden table-corners"
          v-model:server-options="tablePagination"
          v-model:items-per-page="tablePagination.rowsPerPage"
          :headers="props.headers"
          :items="data.items"
          :items-length="data.pagination.total"
          :group-by="props.groupBy"
          :loading="data.loading"
          :show-select="props.showSelect"
          @update:options="sortItems"
          return-object
          table-class-name="customize-vue3-easy-data-table "
          alternating
          hide-default-footer
          item-selectable="selectable">
          <template v-for="(x, slotName) in $slots" #[slotName]="item">
            <slot v-if="slotName.includes('item.')" :name="slotName" v-bind="item" />
          </template>
          <template v-slot:group-header="{ item, columns, toggleGroup, isGroupOpen }" v-if="props.groupBy">
            <slot name="group-header" :item="item" :columns="columns" :toggleGroup="toggleGroup" :isGroupOpen="isGroupOpen">
              <tr>
                <td :colspan="columns.length">
                  <v-btn size="small" variant="text" :icon="isGroupOpen(item) ? '$expand' : '$next'" @click="toggleGroup(item)"></v-btn>
                  <span v-if="$slots['group-header-item']">
                    <slot name="group-header-item" :item="item.items[0].raw" />
                  </span>
                  <span v-else>{{ item.value }}</span>
                </td>
              </tr>
            </slot>
          </template>

          <template #empty-message>
            <span class="text-subtitle-1">{{ t('noDataAvailable') }}</span>
          </template>

          <template #loading>
            <ProgressBar size="50" width="2" color="default" />
          </template>

          <template #bottom>
            <div class="d-flex flex-wrap justify-end align-center ga-3 pa-2 text-secondary bg-secondary-background">
              <div>
                <Selection
                  v-model="tablePagination.rowsPerPage"
                  :disabled="data.loading"
                  density="compact"
                  type="number"
                  min="1"
                  :max-="data.pagination.totalPages"
                  :items="[10, 15, 20, 25, 30, 40, 50, 100]" />
              </div>
              <span> {{ data.pagination.from ?? 0 }} - {{ data.pagination.to ?? 0 }} {{ $t('of') }} {{ data.pagination.total ?? 0 }} </span>
              <div>
                <VTextField
                  :disabled="data.loading"
                  min="1"
                  :max-="data.pagination.totalPages"
                  style="width: 150px"
                  type="number"
                  :prefix="$t('page')"
                  v-model="data.tempPage"
                  @update:model-value="handlePageChange" />
              </div>
              <div class="d-flex align-center ga-1">
                <v-btn
                  v-for="(link, index) in data.pagination.links"
                  :key="index"
                  variant="elevated"
                  size="x-small"
                  rounded="lg"
                  icon
                  :color="link.active ? 'primary' : 'default'"
                  class=""
                  :disabled="data.loading || link.url === null"
                  @click="handlePaginationClick(link)">
                  <span v-if="Number(link.label)">{{ link.label }}</span>
                  <span v-else-if="link.label === '...'">{{ link.label }}</span>
                  <v-icon v-else :icon="link.label.toLowerCase().includes('previous') ? 'mdi-chevron-left' : 'mdi-chevron-right'" />
                </v-btn>
              </div>
            </div>
          </template>
        </v-data-table>
      </VCol>
    </VRow>
  </VCard>
</template>

<script setup>
import { reactive, onMounted, ref, watch, onBeforeUnmount } from 'vue';
import i18n from '@/plugins/i18n';
import { useRouter } from 'vue-router';
import { useSnackBarStore } from '@/stores/snackbar';
import adminAxios from '@/plugins/axios';
import ExportAttributeMenu from '../export/ExportAttributeMenu.vue';
import Selection from '../selectors/Selection.vue';

const router = useRouter();
const notification = useSnackBarStore();

const { t } = i18n.global;
// Props
const props = defineProps({
  baseRoute: { type: [String], required: true },
  headers: { type: Array },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  tTitle: { type: String, default: '' },
  tSubtitle: { type: String, default: '' },
  paginated: { type: [Boolean], default: true },
  enableFilters: { type: Boolean, default: false },
  groupBy: { type: Array },
  filtersData: { type: Object, default: () => ({}) },
  defaultObject: { type: Object, default: () => ({}) },
  exportFiltersData: { type: Object, default: () => ({}) },
  items: { type: Object, default: () => [] },
  searchLabel: { type: String, default: 'searchHere' },
  checkIfEmptyAtFirstLoadOnly: { type: [Boolean], default: false },
  emptyTitle: { type: String, default: 'noData' },
  emptySubtitle: { type: String, default: 'noDataYet' },
  emptyImage: { type: String, default: '' },
  emptyButton: {
    type: Function,
    default: () => {},
  },
  orderBy: { type: String, default: 'id' },
  sortType: { type: String, default: 'desc' },
  enableUrlParams: { type: Boolean, default: false },
  showFilters: { type: Boolean, default: false },
  setFilterFromUrl: { type: Function, default: null },
  enableExportByDate: { type: [Boolean], default: true },
  showSelect: { type: [Boolean], default: false },
  exportModel: { type: String, default: null },
  removeExportFilterData: { type: Boolean, default: false },
  refreshRate: { type: Number, default: 0 },
  showFilterInitialValue: { type: [Boolean], default: false },
  enableSearch: { type: [Boolean], default: true },
  addCustomField: {
    type: Array,
    default: () => [],
    validator: value => {
      if (value.length === 0) return true;
      const isValid = value.length === 2 && typeof value[0] === 'string' && typeof value[1] === 'function';
      if (!isValid) {
        console.warn('addCustomField must be [string, function] when provided');
      }
      return isValid;
    },
  },
  rowsPerPage: {
    type: Number,
    default: 25,
  },
});

const emit = defineEmits(['is-empty', 'dataLoaded', 'getResponse']);

const itemsTimer = ref(null);
const selectedItems = defineModel('selectedItems', {
  type: Array,
  default: () => [],
});

// Data
const data = reactive({
  pagination: {
    total: 0,
    count: 0,
    totalPages: 1,
    from: 0,
    to: 0,
    links: [],
  },
  search: '',
  loading: false,
  loaded: false,
  items: props.items,
  openDeleteDialog: props.showFilters,
  openDeleteDialog: false,
  isFirstLoad: true,
  clearIntervalVal: false,
  timer: null,
  debounceTimeout: null,
  response: {},
  tempPage: 1,
});

const tablePagination = ref({
  page: 1, // Current Page
  sortBy: props.orderBy,
  sortType: props.sortType,
  rowsPerPage: props.rowsPerPage,
});

function handlePageChange(val) {
  data.tempPage = val;

  // Clear previous timeout if it exists
  if (data.debounceTimeout) clearTimeout(data.debounceTimeout);

  // Start a new timeout
  data.debounceTimeout = setTimeout(() => {
    const pageNum = parseInt(val);
    const min = 1;
    const max = data.pagination.totalPages;

    if (!isNaN(pageNum) && pageNum >= min && pageNum <= max) {
      tablePagination.value.page = pageNum;
    }
  }, 300); // 300ms debounce delay
}

// Methods
const getData = () => {
  if (data.clearIntervalVal) {
    clearInterval(data.timer);
    return;
  }
  data.timer = null;
  data.loaded = false;
  data.loading = true;

  if (props.rows != null) {
    data.items = props.rows;
    data.pagination.total = props.rows.length;
  }
  let params = {
    page: tablePagination.value.page,
    per_page: tablePagination.value.rowsPerPage,
    descending: tablePagination.value.sortType == 'desc' ? true : false,
    sort: tablePagination.value.sortBy,
    ...props.defaultObject,
  };
  data.tempPage = tablePagination.value.page;

  if (data.search) {
    params.search = data.search;
  }

  // Filters
  if (props.filtersData) {
    for (const key in props.filtersData) {
      if (props.filtersData[key] !== null && props.filtersData[key] !== '' && key !== 'search') {
        params[key] = props.filtersData[key];
      }
    }
  }
  if (props.enableUrlParams) {
    router.push({ path: router.currentRoute.value.fullPath, query: Object.filter(params, val => !!val) });
  }

  adminAxios
    .get(props.baseRoute, { params })
    .then(response => {
      data.response = response.data;
      data.items = response?.data ? response.data.data : [];
      emit('getResponse', response);
      if (response) {
        response = response.data;
        data.pagination.to = response.to;
        data.pagination.from = response.from;
        data.pagination.to = response.to;
        data.pagination.links = response.links;
        data.pagination.total = response.total ? response.total : data.items.length;
        data.pagination.count = response.per_page ? response.per_page : data.items.length;
        data.pagination.totalPages = response.last_page ? response.last_page : 1;
        tablePagination.value.rowsPerPage = response.per_page
          ? props.groupBy
            ? data.items.length + data.pagination.total
            : response.per_page
          : data.items.length;
        tablePagination.value.page = response.current_page ? response.current_page : 1;
        if (props.addCustomField.length) {
          const [fieldName, transformFn] = props.addCustomField;
          data.items = response.data.map(item => {
            try {
              return {
                ...item,
                [fieldName]: transformFn(item),
              };
            } catch (error) {
              console.error('Error processing item:', item, error);
              return item; // Return original item if transformation fails
            }
          });
        }
        emit('dataLoaded', data.items);
      }
    })
    .catch(notification.responseError)
    .finally(() => {
      data.loading = false;
      data.loaded = true;
      if (data.isFirstLoad) {
        emit('is-empty', data.items.length === 0);
        data.isFirstLoad = false;
      } else if (!props.checkIfEmptyAtFirstLoadOnly) {
        emit('is-empty', data.items.length === 0);
      }
    });
};

const handlePaginationClick = link => {
  if (Number(link.label)) {
    tablePagination.value.page = Number(link.label);
  } else if (link.label.toLowerCase().includes('previous')) {
    tablePagination.value.page -= 1;
  } else if (link.label.toLowerCase().includes('next')) {
    tablePagination.value.page += 1;
  }
};

const paginationRange = (inputPage, lastPage) => {
  if (inputPage && inputPage >= 1 && inputPage <= lastPage) return true;
  return ('pageLimit', lastPage);
};

const onSearch = () => {
  if (itemsTimer.value) {
    clearTimeout(itemsTimer.value);
  }
  itemsTimer.value = setTimeout(() => {
    tablePagination.value.page = 1;
    getData();
  }, 700);
};

// watchers
watch(
  tablePagination,
  async value => {
    if (paginationRange(value.page, data.pagination.totalPages) === true && data.loaded) {
      getData();
    }
  },
  { deep: true },
);

watch(
  () => props.baseRoute,
  value => {
    if (value) {
      getData();
    }
  },
);

watch(
  () => props.filtersData,
  async value => {
    if (value) {
      if (props.paginated) {
        tablePagination.value = {
          page: 1,
          sortBy: props.orderBy,
          sortType: props.sortType,
          rowsPerPage: props.rowsPerPage,
        };
      }
      await getData();
    }
  },
  { deep: true },
);

function sortItems({ sortBy }) {
  if (sortBy.length) {
    tablePagination.value.sortBy = sortBy[0].key;
    tablePagination.value.sortType = sortBy[0].order;
  } else {
    tablePagination.value.sortBy = props.orderBy;
    tablePagination.value.sortType = props.sortType;
  }
}

// Lifecycle
onMounted(() => {
  if (props.enableUrlParams && Object.keys(router.currentRoute.value.query).length) {
    let params = router.currentRoute.value.query;
    if (params?.descending) {
      tablePagination.value.sortType = params.descending ? 'desc' : 'asc';
    }
    if (params?.sort) {
      tablePagination.value.sortBy = params.sort;
    }
    if (params?.page) {
      tablePagination.value.page = parseInt(params?.page);
    }
    if (params?.search) {
      data.search = params?.search;
    }

    if (props.setFilterFromUrl) {
      let filters = { ...props.filtersData };
      Object.keys(filters).map(key => {
        if (params[key]) {
          filters[key] = is_numeric(params[key]) ? parseInt(params[key]) : params[key];
        }
      });
      props.setFilterFromUrl(filters);
    }
  }
  getData();
  if (props.refreshRate > 0) {
    data.timer = setInterval(getData, props.refreshRate);
  }
});

onBeforeUnmount(() => {
  data.clearIntervalVal = true;
  clearInterval(data.timer);
});

// Expose
defineExpose({ getData, data });
</script>

<style lang="scss">
.pagination > * {
  justify-content: flex-start !important;
}

.data-table .subtitle {
  color: rgb(var(--v-theme-text-secondary));
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
